<template>
  <transition name="modal">
    <div class="modal-mask" v-if="gameStore.showGameSummary" @click="closeModal">
      <div class="modal-wrapper">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h3>Game Summary</h3>
          </div>
          
          <div class="modal-body">
            <div class="analytics-grid">
              <!-- Average Round Time -->
              <div class="stat-card" @click="showRoundDetails = !showRoundDetails" style="cursor: pointer;">
                <div class="stat-icon">
                  <i class="fa-solid fa-clock"></i>
                </div>
                <div class="stat-content">
                  <h3>Average Round Time</h3>
                  <div class="stat-value">{{ formatTime(gameStore.averageRoundTime) }}</div>
                  <div class="stat-description">
                    Time from cards dealt to "I'm Done"
                    <br><small>Click to see individual round times</small>
                  </div>
                </div>
              </div>
              
              <!-- Rounds Completed -->
              <div class="stat-card">
                <div class="stat-icon">
                  <i class="fa-solid fa-trophy"></i>
                </div>
                <div class="stat-content">
                  <h3>Rounds Completed</h3>
                  <div class="stat-value">{{ gameStore.gameAnalytics.totalRoundsCompleted }}</div>
                  <div class="stat-description">Number of rounds finished</div>
                </div>
              </div>
              
              <!-- Valid Card Rows -->
              <div class="stat-card" @click="showDebugInfo = !showDebugInfo" style="cursor: pointer;">
                <div class="stat-icon">
                  <i class="fa-solid fa-check-circle"></i>
                </div>
                <div class="stat-content">
                  <h3>Valid Card Rows</h3>
                  <div class="stat-value">{{ totalValidRows }} / {{ totalPossibleRows }} ({{ validRowsPercentage }}%)</div>
                  <div class="stat-description">
                    Valid rows across all rounds
                    <br><small>Click to see round-by-round analysis</small>
                  </div>
                </div>
              </div>
              
              <!-- Game Duration -->
              <div class="stat-card">
                <div class="stat-icon">
                  <i class="fa-solid fa-hourglass-end"></i>
                </div>
                <div class="stat-content">
                  <h3>Total Game Time</h3>
                  <div class="stat-value">{{ formatTime(totalGameTime) }}</div>
                  <div class="stat-description">Time from start to finish</div>
                </div>
              </div>
            </div>
            
            <!-- Round Details Section -->
            <div v-if="showRoundDetails && gameStore.gameAnalytics.roundTimes.length > 0" class="round-details">
              <h4>Individual Round Times:</h4>
              <div class="round-times-list">
                <div v-for="(time, index) in gameStore.gameAnalytics.roundTimes" :key="index" class="round-time-item">
                  <span class="round-label">Round {{ index + 1 }}:</span>
                  <span class="round-time">{{ formatTime(time) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Debug info for Valid Card Rows -->
            <div v-if="showDebugInfo" class="debug-info">
              <h4>Valid Rows History (Each "I'm Done" Press):</h4>
              
              <div v-if="gameStore.gameAnalytics.validRowsHistory.length > 0" class="valid-rows-history">
                <div v-for="(entry, index) in gameStore.gameAnalytics.validRowsHistory" :key="index" class="history-entry">
                  <div class="history-header">
                    <strong>Round {{ entry.round }} - {{ entry.validRows }} valid rows found</strong>
                    <small class="history-time">{{ formatHistoryTime(entry.timestamp) }}</small>
                  </div>
                  
                  <div class="history-cards">
                    <span class="card-rankings">Card order: {{ entry.cardOrder.map(c => `T${c.ranking}`).join('-') }}</span>
                  </div>
                  
                  <div v-if="showDetailedAnalysis[index]" class="detailed-analysis">
                    <h6>Detailed Sequence Analysis:</h6>
                    <div v-for="(sequence, seqIndex) in getSequencesForEntry(entry)" :key="seqIndex" class="sequence-check">
                      <span class="sequence-label">Cards {{ sequence.start + 1 }}-{{ sequence.start + 5 }}:</span>
                      <span class="sequence-cards">{{ sequence.cards.map(c => `T${c.ranking}`).join('-') }}</span>
                      <span :class="['sequence-result', sequence.valid ? 'valid' : 'invalid']">
                        {{ sequence.valid ? '✓ Valid' : '✗ Invalid' }}
                      </span>
                      <span v-if="!sequence.valid" class="sequence-reason">({{ sequence.reason }})</span>
                    </div>
                  </div>
                  
                  <button @click="toggleDetailedAnalysis(index)" class="analysis-toggle">
                    {{ showDetailedAnalysis[index] ? 'Hide Details' : 'Show Details' }}
                  </button>
                </div>
              </div>
              
              <div v-else class="no-history">
                <p>No "I'm Done" presses recorded yet. Play a round and click "I'm Done" to see analysis here!</p>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button class="save" @click="playAgain">
              <i class="fa-solid fa-play"></i>
              Play Again
            </button>
            <button class="cancel" @click="closeModal">
              <i class="fa-solid fa-times"></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useGameStore } from '@/stores/gameStore';

const gameStore = useGameStore();

// Reactive state
const showRoundDetails = ref(false);
const showDebugInfo = ref(false);
const showDetailedAnalysis = ref({}); // Track which history entries show detailed analysis

// Computed properties
const totalGameTime = computed(() => {
  return gameStore.gameAnalytics.totalGameTime;
});

const totalValidRows = computed(() => {
  const history = gameStore.gameAnalytics.validRowsHistory;
  return history.reduce((sum, entry) => sum + entry.validRows, 0);
});

const totalPossibleRows = computed(() => {
  const history = gameStore.gameAnalytics.validRowsHistory;
  return history.reduce((sum, entry) => {
    const cardCount = entry.cardOrder.length;
    const maxPossibleRowsThisRound = Math.max(0, cardCount - 4);
    return sum + maxPossibleRowsThisRound;
  }, 0);
});

const validRowsPercentage = computed(() => {
  const history = gameStore.gameAnalytics.validRowsHistory;
  if (history.length === 0) return 0;
  
  // Calculate total valid rows and total possible rows across all rounds
  let totalValidRows = 0;
  let totalPossibleRows = 0;
  
  history.forEach(entry => {
    const cardCount = entry.cardOrder.length;
    const maxPossibleRowsThisRound = Math.max(0, cardCount - 4); // Number of possible 5-card sequences
    
    totalValidRows += entry.validRows;
    totalPossibleRows += maxPossibleRowsThisRound;
  });
  
  if (totalPossibleRows === 0) return 0;
  
  return Math.round((totalValidRows / totalPossibleRows) * 100);
});

// Methods
const analyzeRow = (row) => {
  if (row.length !== 5) {
    return { valid: false, reason: 'Not 5 cards' };
  }
  
  // Find the minimum tier in the row
  const minTier = Math.min(...row.map(card => card.ranking));
  
  // Check if the first card has the minimum tier
  if (row[0].ranking !== minTier) {
    return { valid: false, reason: `First card (T${row[0].ranking}) is not the minimum tier (T${minTier})` };
  }
  
  // Check if each subsequent card has the same or higher tier
  for (let i = 1; i < row.length; i++) {
    if (row[i].ranking < row[i - 1].ranking) {
      return { valid: false, reason: `Card ${i + 1} (T${row[i].ranking}) is lower than card ${i} (T${row[i - 1].ranking})` };
    }
  }
  
  return { valid: true, reason: 'Valid sequence' };
};

const getSequencesForEntry = (entry) => {
  const cardOrder = entry.cardOrder;
  if (!cardOrder || cardOrder.length < 5) return [];
  
  const sequences = [];
  for (let i = 0; i <= cardOrder.length - 5; i++) {
    const row = cardOrder.slice(i, i + 5);
    const analysis = analyzeRow(row);
    sequences.push({
      start: i,
      cards: row,
      valid: analysis.valid,
      reason: analysis.reason
    });
  }
  return sequences;
};

const toggleDetailedAnalysis = (index) => {
  showDetailedAnalysis.value[index] = !showDetailedAnalysis.value[index];
};

const formatTime = (milliseconds) => {
  if (!milliseconds || milliseconds === 0) return '0s';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

const formatHistoryTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString();
};

const closeModal = () => {
  gameStore.showGameSummary = false;
  showRoundDetails.value = false;
  showDebugInfo.value = false;
  showDetailedAnalysis.value = {};
};

const playAgain = () => {
  closeModal();
  gameStore.restartGame();
};
</script>

<style src="../styles/_modals.scss" lang="scss"></style>

<style lang="scss" scoped>
@import "@/styles/_variables";

// Analytics specific styles
.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-0.125rem);
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.1);
  }
  
  .stat-icon {
    flex-shrink: 0;
    width: 3rem;
    height: 3rem;
    background: $brand-one;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
  }
  
  .stat-content {
    flex: 1;
    
    h3 {
      margin: 0 0 0.25rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.05rem;
    }
    
    .stat-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: $brand-one;
      margin-bottom: 0.25rem;
    }
    
    .stat-description {
      font-size: 0.75rem;
      color: #6c757d;
      line-height: 1.3;
    }
  }
}

// Round details section
.round-details {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  
  h4 {
    margin: 0 0 1rem 0;
    color: $brand-one;
    font-weight: 600;
  }
  
  .round-times-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .round-time-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    background: white;
    border-radius: 0.25rem;
    border: 1px solid #e9ecef;
    
    .round-label {
      font-weight: 600;
      color: $brand-one;
    }
    
    .round-time {
      font-weight: 500;
      color: #6c757d;
    }
  }
}

// Debug info section
.debug-info {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #fff3cd;
  border-radius: 0.5rem;
  border: 1px solid #ffd369;
  
  h4, h5, h6 {
    margin: 0 0 1rem 0;
    color: #856404;
    font-weight: 600;
  }
  
  h5 {
    font-size: 0.875rem;
    margin-top: 1rem;
  }
  
  h6 {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .no-history {
    text-align: center;
    padding: 2rem;
    color: #856404;
    font-style: italic;
  }
}

// Valid rows history section
.valid-rows-history {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-entry {
  background: white;
  border: 1px solid #ffd369;
  border-radius: 0.375rem;
  padding: 1rem;
  
  .history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    
    strong {
      color: #856404;
      font-size: 0.875rem;
    }
    
    .history-time {
      color: #6c757d;
      font-size: 0.75rem;
    }
  }
  
  .history-cards {
    margin-bottom: 0.75rem;
    
    .card-rankings {
      font-family: monospace;
      font-size: 0.75rem;
      color: #495057;
      background: #f8f9fa;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      border: 1px solid #e9ecef;
    }
  }
  
  .detailed-analysis {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #ffd369;
  }
  
  .analysis-toggle {
    background: #856404;
    color: white;
    border: none;
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: darken(#856404, 10%);
    }
  }
}

.sequence-check {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    margin: 0.25rem 0;
    background: white;
    border-radius: 0.25rem;
    border: 1px solid #ffd369;
    font-size: 0.75rem;
    
    .sequence-label {
      min-width: 6rem;
      font-weight: 600;
      color: #856404;
    }
    
    .sequence-cards {
      min-width: 8rem;
      font-family: monospace;
      font-weight: 500;
    }
    
    .sequence-result {
      font-weight: 600;
      
      &.valid {
        color: #155724;
      }
      
      &.invalid {
        color: #721c24;
      }
    }
    
    .sequence-reason {
      font-style: italic;
      color: #721c24;
      flex: 1;
    }
}

// Responsive Design
@media (max-width: 768px) {
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    flex-direction: column;
    text-align: center;
    
    .stat-icon {
      margin-bottom: 0.5rem;
    }
  }
  
  .round-time-item, .sequence-check {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
